import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import endpoints, { getBackendUrl } from '../../../endpoints';
import { storage } from '../../../storage';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { formatDate } from '../../../helpers'
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Confirmation from '../modals/Confirmation';
import ContentSummaryModal from '../modals/ContentSummaryModal';
import CoversModal from '../modals/CoversModal';
import ContentNoteModal from '../modals/ContentNoteModal';
import ProfileMatchModal from '../modals/ProfileMatchModal';
import InfoIcon from '@mui/icons-material/InfoRounded';
import PersonSearchIcon from '@mui/icons-material/PersonSearchRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import { tokens } from '../../../theme';

// The job description is Markdown-ish source text (Upwork sends it with
// backslash-escaped punctuation and real newlines). `breaks: true` turns
// single newlines into <br/> too, since the source relies on plain line
// breaks as much as on blank-line paragraphs.
marked.setOptions({ breaks: true, gfm: true });

export default function FeedContentsTable(props) {
    const [deleteTagItem, setDeleteTagItem] = useState()
    const [contentSummaryItem, setContentSummaryItem] = useState()
    const [coversItem, setCoversItem] = useState()
    const [matchItem, setMatchItem] = useState()
    const [noteItem, setNoteItem] = useState()
    const [expandedIds, setExpandedIds] = useState({})
    const [overflowIds, setOverflowIds] = useState({})
    const contentRefs = useRef({})
    const token = useSelector((state) => state.auth.token)

    const previewLines = props.previewLines || 4

    // Measures which (collapsed) job descriptions actually overflow the
    // preview, so the "Show more" toggle only appears when there's more to read.
    useEffect(() => {
        const next = {}
        props.items?.forEach((row) => {
            const el = contentRefs.current[row.id]
            if (el) {
                next[row.id] = el.scrollHeight > el.clientHeight + 1
            }
        })
        setOverflowIds(next)
    }, [props.items, previewLines])

    const toggleExpanded = (id) => {
        setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    const rawMarkup = (content) => {
        var rawMarkup = content
        return { __html: DOMPurify.sanitize(rawMarkup) };
    }

    const deleteTagHandle = async (response) => {
        if (response) {
            let uri = getBackendUrl(endpoints.tags_content_delete, token)
            uri = uri.replace('{id}', deleteTagItem.tagId)
            uri = uri.replace('{contentId}', deleteTagItem.contentId)

            try {
                await axios.delete(uri)
                props.onReloadItem(deleteTagItem.contentId)
            } catch (e) {
                console.log(e)
            }
        }
        setDeleteTagItem(null)
    }

    const contentNoteModalHandler = (value) => {
        props.onReloadItem(noteItem.id)
        setNoteItem(null);
    }

    const contentCoversModalHandler = () => {
        props.onReloadItem(coversItem.id)
        setCoversItem(null);
    }

    const contentMatchModalHandler = () => {
        if (matchItem?.id) {
            props.onReloadItem(matchItem.id)
        }
        setMatchItem(null)
    }

    // Owner/Profile notes save straight from the inline dropdown, no
    // confirmation dialog like the other (generic) note types.
    const saveNoteInline = async (rowId, note, value) => {
        const uri = getBackendUrl(endpoints.feedcontents_ext_note, token).replace('{id}', rowId)
        try {
            await axios.put(uri, {
                name: note.name,
                value: value,
            })
            props.onReloadItem(rowId)
        } catch (e) {
            console.log(e)
        }
    }

    // Small source mark next to the title. Feeds are Upwork RSS today, but the
    // host is read from the job link so a non-Upwork feed degrades gracefully.
    const sourceMark = (applyUrl) => {
        let host = ''
        try {
            host = new URL(applyUrl).hostname.replace(/^www\./, '')
        } catch (e) {
            host = ''
        }

        const isUpwork = host.endsWith('upwork.com')

        return (
            <Tooltip arrow title={host || 'Open job'}>
                <Box
                    component="a"
                    href={applyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: '9px',
                        border: '1px solid ' + tokens.line,
                        backgroundColor: tokens.surfaceMuted,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'border-color .15s ease',
                        '&:hover': { borderColor: tokens.lineStrong },
                    }}
                >
                    {isUpwork
                        ? <Box component="img" src="/upwork_logo_icon.png" alt="Upwork" sx={{ width: 20, height: 20 }} />
                        : <OpenInNewIcon sx={{ fontSize: 17, color: tokens.inkMuted }} />}
                </Box>
            </Tooltip>
        )
    }

    const pluralizeEn = (n, singular) => `${n} ${singular}${n === 1 ? '' : 's'}`

    // "2 significant units": weeks+days, days+hours, hours+minutes — the
    // smaller unit is dropped once it hits 0. Below a minute, seconds only.
    // Once a post is a week or older we stop drilling below days.
    const formatJobAge = (iso_date) => {
        if (!iso_date) return ''
        const diffMs = Date.now() - new Date(iso_date).getTime()
        if (diffMs < 0) return ''

        const totalSeconds = Math.floor(diffMs / 1000)
        const MINUTE = 60
        const HOUR = 60 * MINUTE
        const DAY = 24 * HOUR
        const WEEK = 7 * DAY

        if (totalSeconds >= WEEK) {
            const weeks = Math.floor(totalSeconds / WEEK)
            const days = Math.floor((totalSeconds % WEEK) / DAY)
            const weekLabel = pluralizeEn(weeks, 'week')
            const dayLabel = days > 0 ? ` ${pluralizeEn(days, 'day')}` : ''
            return `${weekLabel}${dayLabel} ago`
        }
        if (totalSeconds >= DAY) {
            const days = Math.floor(totalSeconds / DAY)
            const hours = Math.floor((totalSeconds % DAY) / HOUR)
            const dayLabel = pluralizeEn(days, 'day')
            return hours > 0
                ? `${dayLabel} and ${pluralizeEn(hours, 'hour')} ago`
                : `${dayLabel} ago`
        }
        if (totalSeconds >= HOUR) {
            const hours = Math.floor(totalSeconds / HOUR)
            const minutes = Math.floor((totalSeconds % HOUR) / MINUTE)
            return minutes > 0 ? `${hours}h ${minutes}m ago` : `${hours}h ago`
        }
        if (totalSeconds >= MINUTE) {
            const minutes = Math.floor(totalSeconds / MINUTE)
            return `${minutes}m ago`
        }
        return `${totalSeconds}s ago`
    }

    const replacePostedOnDate = (iso_date, content) => {
        if (iso_date) {
            const localDate = new Intl.DateTimeFormat('en-GB', {
                dateStyle: "full",
                timeStyle: "medium",
            }).format(new Date(iso_date));

            const age = formatJobAge(iso_date)
            const dateWithAge = age ? `${age}, ${localDate}` : localDate

            return content.replace('%posted_on_date%', dateWithAge);
        }
        return content
    }

    // The backend appends the job's attribute lines (Hourly Range, Posted On,
    // Category, Skills, ...) after the free-text description, each wrapped in
    // <b>. Only the free-text part should be clamped to a preview — the
    // attribute block must always render in full.
    const splitJobContent = (content) => {
        if (!content) return { description: '', meta: '' }
        const metaIndex = content.indexOf('<b>')
        if (metaIndex === -1) return { description: content, meta: '' }

        const description = content.slice(0, metaIndex).replace(/(<br\s*\/?>\s*)+$/i, '')
        const meta = content.slice(metaIndex)
        return { description, meta }
    }

    // The description half is Markdown source (bold, bullet/numbered lists,
    // paragraphs, escaped punctuation) — render it as such instead of dumping
    // the raw markers as plain text.
    const descriptionMarkup = (description) => {
        return { __html: DOMPurify.sanitize(marked.parse(description)) };
    }

    return (
        <Stack spacing={1}>
            <Stack spacing={1.5} direction="row" alignItems="center" sx={{ px: 0.5, pb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                    Total items:
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', ml: '4px!important' }}>
                    {props.total ?? '—'}
                </Typography>
                {props.headerContent}
            </Stack>
            <Stack>
                {props.items?.map((row, key) => (
                    <Paper
                        key={'CONT_' + row.id}
                        sx={{
                            p: { xs: 2.5, sm: 3.5 },
                            mb: '10px',
                            // Unread jobs get an accent rail + a whisper of tint
                            // instead of the old flat yellow fill.
                            borderLeft: '3px solid',
                            borderLeftColor: (row.read_at || row.ext_read_at) ? 'transparent' : tokens.accent,
                            backgroundColor: (row.read_at || row.ext_read_at) ? tokens.surface : 'rgba(109, 94, 248, 0.028)',
                            overflowX: 'hidden',
                            wordWrap: 'break-word',
                        }}
                        className="feedContentRow"
                        data-id={row.id}
                    >
                        <Stack spacing={1.25}>
                            <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={1.5}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ rowGap: 1 }}
                            >
                                {sourceMark(row.apply_url)}
                                <Link
                                    href={row.apply_url}
                                    target="_blank"
                                    underline="none"
                                    sx={{
                                        color: tokens.ink,
                                        '&:hover': { color: tokens.accent },
                                        flex: '1 1 240px',
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 }}>
                                        {row.title}
                                    </Typography>
                                </Link>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    justifyContent="flex-end"
                                    sx={{ rowGap: 1, ml: 'auto' }}
                                >
                                    {row.summary ?
                                        <Tooltip arrow title="AI Processing Details">
                                            <Chip
                                                label={row.suitable}
                                                size="medium"
                                                color={row.suitable ? (row.suitable >= props.suitableThreshold ? 'error' : 'info') : 'default'}
                                                icon={<InfoIcon />}
                                                onClick={() => { setContentSummaryItem(row) }}
                                            />
                                        </Tooltip>
                                        : null}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PersonSearchIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => { setMatchItem(row) }}
                                        sx={{ textTransform: 'none', fontSize: 13, borderRadius: '10px', height: 40 }}
                                    >
                                        Match
                                    </Button>
                                    {row.notes?.map((note, key) => {
                                        if (note.type === 'checkbox') {
                                            return (
                                                <FormControlLabel
                                                    key={key}
                                                    sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            sx={{ p: 0.5 }}
                                                            checked={!!note.value}
                                                            onChange={() => { setNoteItem({ id: row.id, note: note }) }}
                                                        />
                                                    }
                                                    label="Sent"
                                                />
                                            )
                                        }
                                        if (note.type === 'bool' && note.options) {
                                            const labelId = 'note-' + row.id + '-' + note.name
                                            return (
                                                <FormControl key={key} size="small" sx={{ minWidth: 120 }}>
                                                    <InputLabel id={labelId} sx={{ fontSize: 13 }}>{note.title}</InputLabel>
                                                    <Select
                                                        labelId={labelId}
                                                        label={note.title}
                                                        value={note.value || ''}
                                                        onChange={(e) => { saveNoteInline(row.id, note, e.target.value) }}
                                                        sx={{ fontSize: 13 }}
                                                    >
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        {note.options?.filter(Boolean).map((option) => (
                                                            <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>{option}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )
                                        }
                                        return (
                                            <Chip
                                                key={key}
                                                size="small"
                                                color={note.value ? 'success' : 'default'}
                                                variant={note.value ? 'filled' : 'outlined'}
                                                label={(note.value ? note.title + ': ' + note.value : note.title)}
                                                onClick={() => { setNoteItem({ id: row.id, note: note }) }}
                                            />
                                        )
                                    })}
                                    <FormControlLabel
                                        sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={{ p: 0.5 }}
                                                checked={!!(row.read_at || row.ext_read_at)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        props.onMarkRead(row.id)
                                                    } else {
                                                        props.onMarkUnread(row.id)
                                                    }
                                                }}
                                            />
                                        }
                                        label="Read"
                                    />
                                </Stack>
                            </Stack>
                            {row.tags?.length ? (
                                <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                                    {row.tags.map((tag, key) => (
                                        <Chip key={key} label={tag.name} size="small" onDelete={() => {
                                            setDeleteTagItem({ contentId: row.id, tagId: tag.id })
                                        }} />
                                    ))}
                                </Stack>
                            ) : null}
                            {(() => {
                                const { description, meta } = splitJobContent(replacePostedOnDate(row.iso_date, row.content))
                                return (
                                    <Box className="jobContent">
                                        <Box
                                            ref={(el) => { contentRefs.current[row.id] = el }}
                                            className={'jobDescriptionText' + (expandedIds[row.id] ? '' : ' collapsed')}
                                            onClick={(e) => {
                                                if (!(overflowIds[row.id] || expandedIds[row.id])) return
                                                if (e.target.closest('a')) return
                                                toggleExpanded(row.id)
                                            }}
                                            sx={{ cursor: (overflowIds[row.id] || expandedIds[row.id]) ? 'pointer' : 'default' }}
                                            style={expandedIds[row.id] ? undefined : { WebkitLineClamp: previewLines }}
                                            dangerouslySetInnerHTML={descriptionMarkup(description)}
                                        />
                                        {overflowIds[row.id] || expandedIds[row.id] ? (
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); toggleExpanded(row.id) }}
                                                sx={{
                                                    display: 'block',
                                                    mt: 0.5,
                                                    border: 'none',
                                                    background: 'none',
                                                    p: 0,
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: tokens.accent,
                                                    cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                            >
                                                {expandedIds[row.id] ? 'Show less' : 'Show more'}
                                            </Box>
                                        ) : null}
                                        {meta ? (
                                            <Box
                                                className="jobContentMeta"
                                                dangerouslySetInnerHTML={rawMarkup(meta)}
                                            />
                                        ) : null}
                                    </Box>
                                )
                            })()}
                        </Stack>
                    </Paper>
                )
                )}
                {props.items?.length || !props.loadingFinish ? null :
                    <Paper sx={{ p: 5, textAlign: 'center' }}>
                        No data to display
                    </Paper>
                }
            </Stack>
            <Confirmation data={deleteTagItem} onResponse={deleteTagHandle} />
            <ContentSummaryModal
                data={contentSummaryItem}
                onClose={() => { setContentSummaryItem(null) }}
            />
            <CoversModal
                data={coversItem}
                onClose={contentCoversModalHandler}
            />
            <ProfileMatchModal
                data={matchItem}
                onClose={() => { setMatchItem(null) }}
                onAssigned={contentMatchModalHandler}
            />
            <ContentNoteModal
                data={noteItem}
                onClose={() => { setNoteItem(null) }}
                onSave={(value) => { contentNoteModalHandler(value) }}
            />
        </Stack>
    );
}
